import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Send, Loader } from 'lucide-react';
import './GenerationQuestionnaire.css';
import { API_URL } from '../config';
import { getValidAccessToken } from '../Utility/authUtils';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'system';
  timestamp: Date;
}

interface QuestionnaireState {
  prompt: string;
}

interface AddressData {
  address_line_1: string;
  address_line_2: string;
  address_line_3: string;
  city: string;
  state: string;
  country: string;
}

const GenerationQuestionnaire: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as QuestionnaireState;

  const [messages, setMessages] = useState<Message[]>([]);
  const [currentInput, setCurrentInput] = useState('');
  const [currentStep, setCurrentStep] = useState(0);
  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);

  // Project data being collected
  const [projectData, setProjectData] = useState({
    project_name: '',
    address_line_1: '',
    address_line_2: '',
    address_line_3: '',
    city: '',
    state: '',
    country: '',
    budget: '',
    currency: 'USD'
  });

  // Address form state
  const [addressData, setAddressData] = useState<AddressData>({
    address_line_1: '',
    address_line_2: '',
    address_line_3: '',
    city: '',
    state: '',
    country: ''
  });
  const [showAddressForm, setShowAddressForm] = useState(false);

  // Generation state
  const [isGenerating, setIsGenerating] = useState(true);
  const [generatedPlans, setGeneratedPlans] = useState<any>(null);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [projectId, setProjectId] = useState<string | null>(null);
  const [isProjectCreated, setIsProjectCreated] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const generationStartedRef = useRef<boolean>(false); // Track if generation already started

  const questions = [
    "Thank you! To get started, what would you like to name this project?",
    "Great! What is the address for this project?",
    "Perfect! What is your estimated budget for this project?"
  ];

  const currencies = [
    { value: 'USD', label: 'USD', symbol: '$' },
    { value: 'EUR', label: 'EUR', symbol: '€' },
    { value: 'INR', label: 'INR', symbol: '₹' },
    { value: 'GBP', label: 'GBP', symbol: '£' },
    { value: 'JPY', label: 'JPY', symbol: '¥' },
    { value: 'AUD', label: 'AUD', symbol: '$' },
    { value: 'CAD', label: 'CAD', symbol: '$' }
  ];

  // Redirect if no prompt provided
  useEffect(() => {
    if (!state?.prompt) {
      navigate('/', { replace: true });
      return;
    }

    // Initialize with user's prompt and first system message
    const initialMessages: Message[] = [
      {
        id: '1',
        text: state.prompt,
        sender: 'user',
        timestamp: new Date()
      },
      {
        id: '2',
        text: questions[0],
        sender: 'system',
        timestamp: new Date()
      }
    ];
    setMessages(initialMessages);

    // Start generation in background only once
    startGeneration();

    // Clear the prompt from navigation state to prevent re-generation
    // Replace the current history entry without the prompt
    navigate('/generation-questionnaire', { 
      replace: true,
      state: {} 
    });
  }, []); // Empty dependency array ensures this runs only once on mount

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Show address form automatically when step 1 is reached
  useEffect(() => {
    if (currentStep === 1 && !showAddressForm) {
      setShowAddressForm(true);
    }
  }, [currentStep]);

  const FEET_TO_UNITS = 10;

  const scalePlanGeometryForRender = <T = any,>(plan: T, scale = FEET_TO_UNITS): T => {
    const isPoint = (v: any) =>
      v && typeof v === "object" && typeof v.x === "number" && typeof v.z === "number";

    const visit = (node: any): any => {
      if (Array.isArray(node)) return node.map(visit);
      if (node && typeof node === "object") {
        if (isPoint(node)) return { x: node.x * scale, z: node.z * scale };
        const out: any = {};
        for (const k of Object.keys(node)) out[k] = visit(node[k]);
        return out;
      }
      return node;
    };

    return visit(plan);
  };

  const startGeneration = async () => {
    // Prevent multiple generation requests
    if (generationStartedRef.current) {
      console.log('Generation already started, skipping duplicate request');
      return;
    }
    
    generationStartedRef.current = true;
    const token = await getValidAccessToken();

    try {
      const response = await fetch(`${API_URL}/projects/plans/ai-generate-prompt-image/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ prompt: state.prompt }),
      });

      if (!response.ok) {
        throw new Error('Generation failed');
      }

      const data = await response.json();
      const floorPlans = data.floor_plans;
      const floorPlansForRender = floorPlans.map((plan: any) => scalePlanGeometryForRender(plan, 10));

      setGeneratedPlans(floorPlansForRender);
      setIsGenerating(false);
      generationStartedRef.current = false; // Reset on error so user can retry

      // Check if we can navigate to proposals
      if (isProjectCreated && projectId) {
        navigateToProposals(floorPlansForRender, projectId);
      }
    } catch (error) {
      console.error("Error generating plans:", error);
      setGenerationError("Failed to generate plans. Please try again.");
      setIsGenerating(false);
    }
  };

  const navigateToProposals = (plans: any, projectIdValue: string) => {
    navigate("/proposals", {
      state: {
        projectData: {
          id: projectIdValue,
          name: projectData.project_name || "AI Drafts",
          budget: projectData.budget || "0",
          currency: projectData.currency,
        },
        roomData: null,
        aiGeneratedPlans: plans,
        aiMode: true,
      },
    });
  };

  const handleAddressSubmit = () => {
    // Validate at least address line 1 is filled
    if (!addressData.address_line_1.trim()) {
      alert('Please enter at least Address Line 1');
      return;
    }

    // Format address for display
    const addressParts = [
      addressData.address_line_1,
      addressData.address_line_2,
      addressData.address_line_3,
      addressData.city,
      addressData.state,
      addressData.country
    ].filter(part => part.trim() !== '');
    
    const fullAddress = addressParts.join(', ');

    // Create user message with formatted address
    const userMessage: Message = {
      id: Date.now().toString(),
      text: fullAddress,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setShowAddressForm(false);
    setIsWaitingForResponse(true);

    // Store address data
    setProjectData(prev => ({
      ...prev,
      address_line_1: addressData.address_line_1,
      address_line_2: addressData.address_line_2,
      address_line_3: addressData.address_line_3,
      city: addressData.city,
      state: addressData.state,
      country: addressData.country
    }));

    // Move to next step
    const nextStep = currentStep + 1;

    setTimeout(() => {
      const systemMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: questions[nextStep],
        sender: 'system',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, systemMessage]);
      setCurrentStep(nextStep);
      setIsWaitingForResponse(false);
    }, 500);
  };

  const handleSendMessage = async () => {
    if (!currentInput.trim() || isWaitingForResponse) return;

    // For budget step, validate it's a number
    if (currentStep === 2) {
      const budgetValue = currentInput.replace(/[^0-9.]/g, '');
      if (!budgetValue || isNaN(Number(budgetValue))) {
        alert('Please enter a valid number for budget');
        return;
      }
    }

    const userMessage: Message = {
      id: Date.now().toString(),
      text: currentStep === 2 ? `${currentInput} ${projectData.currency}` : currentInput,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const inputValue = currentInput; // Store current input before clearing
    setCurrentInput('');
    setIsWaitingForResponse(true);

    // Store the answer
    let updatedProjectData = { ...projectData };
    if (currentStep === 0) {
      updatedProjectData.project_name = inputValue;
      setProjectData(updatedProjectData);
    } else if (currentStep === 2) {
      updatedProjectData.budget = inputValue.replace(/[^0-9.]/g, '');
      setProjectData(updatedProjectData);
    }

    // Move to next step
    const nextStep = currentStep + 1;

    // Small delay for better UX
    setTimeout(() => {
      if (nextStep < questions.length) {
        const systemMessage: Message = {
          id: (Date.now() + 1).toString(),
          text: questions[nextStep],
          sender: 'system',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, systemMessage]);
        setCurrentStep(nextStep);
        setIsWaitingForResponse(false);
      } else {
        // All questions answered
        const finalMessage: Message = {
          id: (Date.now() + 1).toString(),
          text: "Thank you! Creating your project...",
          sender: 'system',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, finalMessage]);
        
        // Create project with the updated data directly
        setTimeout(() => {
          createProjectWithData(updatedProjectData);
        }, 100);
      }
    }, 500);
  };

  const createProject = async () => {
    await createProjectWithData(projectData);
  };

  const createProjectWithData = async (data: typeof projectData) => {
    const token = await getValidAccessToken();

    try {
      const apiData = {
        project_name: data.project_name.trim(),
        address_line_1: data.address_line_1.trim(),
        address_line_2: data.address_line_2.trim() || undefined,
        address_line_3: data.address_line_3.trim() || undefined,
        city: data.city.trim() || undefined,
        state: data.state.trim() || undefined,
        country: data.country.trim() || undefined,
        estimated_budget: parseFloat(data.budget) || 0,
        currency: data.currency
      };

      console.log('Creating project with budget:', apiData.estimated_budget); // Debug log

      const response = await fetch(`${API_URL}/projects/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(apiData)
      });

      if (!response.ok) {
        throw new Error('Project creation failed');
      }

      const createdProject = await response.json();
      setProjectId(createdProject.id);
      setIsProjectCreated(true);
      setIsWaitingForResponse(false);

      // Check if we can navigate to proposals
      if (generatedPlans && !isGenerating) {
        navigateToProposals(generatedPlans, createdProject.id);
      }
    } catch (error) {
      console.error("Error creating project:", error);
      const errorMessage: Message = {
        id: Date.now().toString(),
        text: "There was an error creating your project. Please try again.",
        sender: 'system',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
      setIsWaitingForResponse(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleBudgetInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow numbers and decimal point
    const value = e.target.value.replace(/[^0-9.]/g, '');
    setCurrentInput(value);
  };

  // Check if both generation and project creation are complete
  useEffect(() => {
    if (generatedPlans && !isGenerating && isProjectCreated && projectId) {
      navigateToProposals(generatedPlans, projectId);
    }
  }, [generatedPlans, isGenerating, isProjectCreated, projectId]);

  return (
    <div className="generation-questionnaire">
      <div className="questionnaire-container">
        {/* Status Panel - Right side on desktop, hidden on mobile */}
        <div className="status-panel">
          <div className="status-content">
            <div className="status-header">
              <h2>Generating Your Floor Plans</h2>
              <p>While we work on your designs, please provide some details about your project.</p>
            </div>

            <div className="status-items">
              <div className={`status-item ${isGenerating ? 'active' : 'complete'}`}>
                <div className="status-icon">
                  {isGenerating ? (
                    <Loader className="spinning" size={24} />
                  ) : (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  )}
                </div>
                <div className="status-text">
                  <h3>AI Generation</h3>
                  <p>{isGenerating ? 'Creating floor plans...' : 'Floor plans ready!'}</p>
                </div>
              </div>

              <div className={`status-item ${currentStep >= questions.length ? (isProjectCreated ? 'complete' : 'active') : ''}`}>
                <div className="status-icon">
                  {currentStep >= questions.length && !isProjectCreated ? (
                    <Loader className="spinning" size={24} />
                  ) : currentStep >= questions.length && isProjectCreated ? (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  ) : (
                    <div className="status-number">{currentStep + 1}/{questions.length}</div>
                  )}
                </div>
                <div className="status-text">
                  <h3>Project Details</h3>
                  <p>
                    {currentStep >= questions.length 
                      ? (isProjectCreated ? 'Project created!' : 'Creating project...')
                      : `Question ${currentStep + 1} of ${questions.length}`
                    }
                  </p>
                </div>
              </div>
            </div>

            {generationError && (
              <div className="error-message">
                <p>{generationError}</p>
              </div>
            )}
          </div>
        </div>

        {/* Chat Panel - Left side on desktop, full screen on mobile */}
        <div className="chat-panel">
          <div className="chat-header">
            <h2>Project Details</h2>
            <p>Help us understand your project better</p>
          </div>

          <div className="chat-messages" ref={chatContainerRef}>
            {messages.map((message) => (
              <div 
                key={message.id} 
                className={`message ${message.sender}`}
              >
                <div className="message-content">
                  <p>{message.text}</p>
                </div>
                <div className="message-time">
                  {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {currentStep < questions.length && !showAddressForm && (
            <div className="chat-input-container">
              {currentStep === 2 ? (
                <div className="budget-input-wrapper">
                  <input
                    type="text"
                    className="chat-input budget-input"
                    placeholder="Enter budget amount"
                    value={currentInput}
                    onChange={handleBudgetInput}
                    onKeyPress={handleKeyPress}
                    disabled={isWaitingForResponse}
                  />
                  <select
                    className="currency-dropdown"
                    value={projectData.currency}
                    onChange={(e) => setProjectData({...projectData, currency: e.target.value})}
                    disabled={isWaitingForResponse}
                  >
                    {currencies.map(curr => (
                      <option key={curr.value} value={curr.value}>
                        {curr.label}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <input
                  type="text"
                  className="chat-input"
                  placeholder="Type your answer..."
                  value={currentInput}
                  onChange={(e) => setCurrentInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={isWaitingForResponse}
                />
              )}
              <button 
                className="send-button"
                onClick={handleSendMessage}
                disabled={!currentInput.trim() || isWaitingForResponse}
              >
                <Send size={20} />
              </button>
            </div>
          )}

          {showAddressForm && (
            <div className="address-form-container">
              <div className="address-form">
                <div className="address-form-fields">
                  <div className="form-group-compact">
                    <input
                      type="text"
                      className="address-input-compact"
                      placeholder="Address Line 1*"
                      value={addressData.address_line_1}
                      onChange={(e) => setAddressData({...addressData, address_line_1: e.target.value})}
                    />
                  </div>

                  <div className="form-group-compact">
                    <input
                      type="text"
                      className="address-input-compact"
                      placeholder="Address Line 2"
                      value={addressData.address_line_2}
                      onChange={(e) => setAddressData({...addressData, address_line_2: e.target.value})}
                    />
                  </div>

                  <div className="form-group-compact">
                    <input
                      type="text"
                      className="address-input-compact"
                      placeholder="Address Line 3"
                      value={addressData.address_line_3}
                      onChange={(e) => setAddressData({...addressData, address_line_3: e.target.value})}
                    />
                  </div>

                  <div className="form-row-compact">
                    <input
                      type="text"
                      className="address-input-compact"
                      placeholder="City"
                      value={addressData.city}
                      onChange={(e) => setAddressData({...addressData, city: e.target.value})}
                    />

                    <input
                      type="text"
                      className="address-input-compact"
                      placeholder="State"
                      value={addressData.state}
                      onChange={(e) => setAddressData({...addressData, state: e.target.value})}
                    />
                  </div>

                  <div className="form-group-compact">
                    <input
                      type="text"
                      className="address-input-compact"
                      placeholder="Country"
                      value={addressData.country}
                      onChange={(e) => setAddressData({...addressData, country: e.target.value})}
                    />
                  </div>
                </div>

                <div className="address-form-actions-compact">
                  <button 
                    className="cancel-btn-compact"
                    onClick={() => {
                      setShowAddressForm(false);
                      setAddressData({
                        address_line_1: '',
                        address_line_2: '',
                        address_line_3: '',
                        city: '',
                        state: '',
                        country: ''
                      });
                    }}
                  >
                    Cancel
                  </button>
                  <button 
                    className="submit-btn-compact"
                    onClick={handleAddressSubmit}
                  >
                    Submit
                  </button>
                </div>
              </div>
            </div>
          )}

          {currentStep >= questions.length && (
            <div className="completion-message">
              <Loader className="spinning" size={24} />
              <p>Processing your information...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GenerationQuestionnaire;
